/* eslint-disable react/forbid-prop-types */
import { useState, useEffect, useRef } from 'preact/hooks';
import { Fragment } from 'preact';
import PropTypes from 'prop-types';

import d from '@hooks/useIsoDateFormat';
import useWindowDimensions from '@hooks/useWindowDimensions';

import Card from '@distinct-components/cards/baseCard';
import BaseEmptyState from '@distinct-components/feedback/baseEmptyState';
import Text from '@ui-kit/typography/text';
import Box from '@ui-kit/box';
import Spinner from '@ui-kit/loaders/Spinner';
import TableControls from '@ui-kit/table/tableControls';
import Table from '@ui-kit/table';
import TableHeader from '@ui-kit/table/tableHeader';
import TableRow from '@ui-kit/table/tableRow';
import TableCell from '@ui-kit/table/tableCell';
import TablePagination from '@ui-kit/table/tablePagination';
import Logo from '@ui-kit/logo/Logo';

import { TableSearchEmptyPrompt, TableIllustrationWrapper } from './ExpiredPublisherConnectionsStyles';

function ExpiredReps({ setSelectedPub, isLoading, pubs }) {
  const [searchActive, setSearchActive] = useState(false);
  const [expiredPubs, setExpiredPubs] = useState(pubs);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { height } = useWindowDimensions();
  const pageRef = useRef(null);

  useEffect(() => {
    if (height < 1080) {
      setItemsPerPage(7);
    }
  }, []);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentResults = expiredPubs.slice(indexOfFirst, indexOfLast);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      pageRef.current.scrollIntoView({ behavior: 'instant', block: 'center' });
    }, 200);
  };

  const onSearchResults = (query) => {
    const results = [];
    setCurrentPage(1);
    if (query) {
      pubs.forEach((r) => {
        const name = `${r.firstName} ${r.lastName}`;
        if (name.toLocaleLowerCase().includes(query.toLocaleLowerCase())) {
          results.push(r);
        }
      });
      setExpiredPubs(results);
      setSearchActive(true);
    } else {
      setExpiredPubs(pubs);
      setSearchActive(false);
    }
  };

  return (
    isLoading ? (
      <Box width="100%" display="flex" justifyContent="center" height="calc(100vh - 200px)" alignItems="center">
        <Spinner size="2em" variant="page" />
      </Box>
    ) : (
      <Box>
        <div ref={pageRef} />
        {pubs.length === 0 ? (
          <Card variant="panel" mt="1.5em">
            <BaseEmptyState
              title="No Inactive Connections"
              message="If a publisher connection expires, you can issue a new credential from this page"
              illustration="composition"
              minHeight="38em"
            />
          </Card>
        ) : (
          <Fragment>
            <TableControls
              itemName="Representative"
              tableData={pubs}
              currentResults={currentResults}
              indexOfFirst={indexOfFirst}
              indexOfLast={indexOfLast}
              showSearch
              searchActive={searchActive}
              searchResults={expiredPubs}
              handleSearch={onSearchResults}
            />
            <Table mb="1em">
              <TableHeader>
                <TableCell widthPercent="48%" pr="0.75em">Representative Name</TableCell>
                <TableCell>Credential Expired</TableCell>
              </TableHeader>
              {currentResults
                .map((pub) => (
                  <TableRow onClick={() => setSelectedPub(pub)} cursor>
                    <TableCell widthPercent="48%" pr="0.75em">
                      <TableIllustrationWrapper>
                        <Box mr="1em">
                          <Logo mb="1em" orgImageURL={pub.logoUrl} />
                        </Box>
                        <Box pt="0.25rem">
                          <Text fontSize="0.938rem" color="var(--text-primary)" fontWeight="600" lineHeight="1.2">
                            {pub.name}
                          </Text>
                        </Box>
                      </TableIllustrationWrapper>
                    </TableCell>
                    <TableCell mobileLabel="Expired:">{d(pub.inactiveDate)}</TableCell>
                  </TableRow>
                ))}
              {(searchActive && currentResults.length === 0)
              && (
                <TableSearchEmptyPrompt>
                  <Text color="var(--text-muted-panel)">No Publisher Connections Match Your Search</Text>
                </TableSearchEmptyPrompt>
              )}
            </Table>
            <TablePagination
              itemsPerPage={itemsPerPage}
              totalItems={expiredPubs.length}
              paginate={paginate}
              currentPage={currentPage}
            />
          </Fragment>
        )}

      </Box>
    )
  );
}

ExpiredReps.propTypes = {
  setSelectedPub: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  pubs: PropTypes.array.isRequired,
};

export default ExpiredReps;
